/**
 * PositionLevel 타입 정의
 *
 * 직위(호칭) 마스터 데이터 타입입니다.
 * 사원, 대리, 과장, 차장, 부장, 수석, 이사, 대표 등의 직위를 관리합니다.
 *
 * **용도:**
 * - 직원 상세/목록/조직도에서의 직위 표기
 * - 직위별 정렬 (rankWeight 기준)
 * - 향후 "진급 대상자 자동 산출" 기능에서 기준값으로 사용
 *
 * **데이터 소스:**
 * - Firestore `positionLevels` 컬렉션
 */

import { DocumentData, QueryDocumentSnapshot, Timestamp } from 'firebase/firestore';

/**
 * 직위(PositionLevel) 타입
 *
 * UI에서 필요한 직위 정보를 담는 인터페이스입니다.
 */
export interface PositionLevel {
  // 필수 필드
  /** Firestore 문서 ID (예: pos_staff, pos_manager) */
  id: string;

  /** 직위명 한글 (UI 표기, 예: 사원, 대리, 과장) */
  name: string;

  /** 직위명 영문 (예: Staff, Assistant Manager) */
  nameEn?: string;

  // 코드/정렬 관련
  /** 시스템 코드 (영문 대문자, 예: STAFF, MANAGER) */
  code?: string;

  /** 정렬 순서 (화면 표시 순서, 작은 값이 먼저 표시) */
  order: number;

  /**
   * 위계 가중치
   *
   * 숫자가 클수록 높은 직위를 의미합니다.
   * - 예: 사원=1, 대리=2, 과장=3, 차장=4, 부장=5, 수석=6, 이사=7, 대표=8
   *
   * **용도:**
   * - 직원 목록/조직도에서 직위별 정렬
   * - 높은 직위 우선 정렬 시 rankWeight 내림차순 사용
   */
  rankWeight: number;

  // 승진 기준 관련
  /**
   * 해당 직위 획득에 필요한 인정 근무연한 (년 단위)
   *
   * **인정 근무연한 = 정규직 입사일 + 학력 연한**
   *
   * - null: 근무연한 기준이 없는 직위 (임명직)
   * - 숫자: 해당 연한 이상이면 자동 진급 대상
   *
   * **예시:**
   * - 사원: null (신입 기본 직위)
   * - 대리: 6 (인정 근무연한 6년 이상)
   * - 과장: 9
   * - 차장: 13
   * - 부장: 16
   * - 수석/이사/대표: null (임명직)
   *
   * **향후 활용:**
   * - Chapter 5-2에서 "진급 대상자 자동 산출" 기능 구현 시 사용
   * - 직원의 인정 근무연한과 비교하여 진급 대상 여부 판단
   */
  requiredRecognizedYearsFromHire: number | null;

  /**
   * 다음 직위로 진급하기 위한 기준 연차 (V20 신규)
   *
   * 현재 직위에서 다음 직위로 진급하기 위해 필요한 연차입니다.
   * "최종진급일(currentPositionStartDate)"이 있는 직원은 이 값을 직위 연차와 비교합니다.
   *
   * - null: 자동 진급 대상 아님 (임명직: 수석/이사/대표)
   * - 숫자: 해당 연차 이상이면 다음 직위 진급 후보
   *
   * **기준 연차:**
   * - 대리 → 과장: 6년
   * - 과장 → 차장: 9년
   * - 차장 → 부장: 13년
   * - 부장 → 수석: 16년
   * - 수석/이사/대표: null (자동 진급 X)
   */
  requiredYearsForNextPosition?: number | null;

  /**
   * 진급 룰 (향후 자동 진급 엔진에서 활용)
   * V50.1: 모든 필드를 optional로 변경 (Firestore 부분 업데이트 지원)
   */
  promotionRule?: {
    /** 자동 진급 여부 (현재는 false 유지) */
    autoPromotion?: boolean;
    /** 다음 직위 ID */
    nextPositionId?: string | null;
    /** 이 직위에서 다음 직위로 진급하기 위한 기준 연한 */
    baseCompanyYearsRequired?: number | null;
    /** 진급 룰 설명 */
    notes?: string;
    /**
     * V50: 사원→대리 학력별 진급 기준 (년 단위)
     * 사원 직위에서만 사용
     */
    staffToAssistantYearsByEducation?: {
      /** 고졸 기준 (기본 6년) */
      highSchool?: number;
      /** 초대졸 2년 기준 (기본 4년) */
      college2?: number;
      /** 초대졸 3년 기준 (기본 3년) */
      college3?: number;
      /** 대졸 기준 (기본 2년) */
      university?: number;
    };
  };

  // 상태
  /** 사용 여부 (active: 사용, inactive: 미사용) */
  status: 'active' | 'inactive';

  /** 직위 설명 (선택) */
  description?: string;

  // 메타데이터
  /** 생성 일시 */
  createdAt?: Timestamp;

  /** 수정 일시 */
  updatedAt?: Timestamp;

  /** 마지막 수정자 (userId) */
  updatedBy?: string;
}

/**
 * 직위 목록 필터
 *
 * usePositionLevels Hook에서 사용하는 필터 파라미터입니다.
 */
export interface PositionLevelListFilter {
  /** 검색어 (직위명, 코드) */
  searchText?: string;

  /** 사용 여부 필터 ('all' | 'active' | 'inactive') */
  status?: string;
}

/**
 * 직위 생성 입력
 *
 * 새로운 직위를 생성할 때 필요한 데이터입니다.
 */
export interface CreatePositionLevelInput {
  /** 직위명 (필수) */
  name: string;

  /** 시스템 코드 (선택) */
  code?: string;

  /** 정렬 순서 (선택, 미입력 시 자동 계산) */
  order?: number;

  /** 위계 가중치 (필수) */
  rankWeight: number;

  /** 승진 기준 인정 근무연한 (선택) */
  requiredRecognizedYearsFromHire?: number | null;

  /** 사용 여부 (선택, 기본값: 'active') */
  status?: 'active' | 'inactive';

  /** 직위 설명 (선택) */
  description?: string;
}

/**
 * 직위 수정 입력
 *
 * 기존 직위를 수정할 때 필요한 데이터입니다.
 * 모든 필드가 선택적이며, 제공된 필드만 업데이트됩니다.
 */
export interface UpdatePositionLevelInput {
  /** 직위명 */
  name?: string;

  /** 시스템 코드 */
  code?: string;

  /** 정렬 순서 */
  order?: number;

  /** 위계 가중치 */
  rankWeight?: number;

  /** 승진 기준 인정 근무연한 */
  requiredRecognizedYearsFromHire?: number | null;

  /** 사용 여부 */
  status?: 'active' | 'inactive';

  /** 직위 설명 */
  description?: string;

  /**
   * V50.1: 진급 룰 (사원→대리 학력별 기준 포함)
   */
  promotionRule?: {
    autoPromotion?: boolean;
    nextPositionId?: string | null;
    baseCompanyYearsRequired?: number | null;
    notes?: string;
    /** V50: 사원→대리 학력별 진급 기준 (년 단위) */
    staffToAssistantYearsByEducation?: {
      highSchool?: number;
      college2?: number;
      college3?: number;
      university?: number;
    };
  };
}

/**
 * Firestore 문서를 PositionLevel 타입으로 변환
 *
 * Firestore의 `positionLevels` 컬렉션 문서를 PositionLevel 인터페이스에 맞게 매핑합니다.
 *
 * @param doc Firestore QueryDocumentSnapshot
 * @returns PositionLevel 객체
 *
 * @example
 * const snapshot = await getDocs(collection(db, 'positionLevels'));
 * const positionLevels = snapshot.docs.map(mapPositionLevelDocToPositionLevel);
 */
export function mapPositionLevelDocToPositionLevel(
  doc: QueryDocumentSnapshot<DocumentData>
): PositionLevel {
  const data = doc.data();

  return {
    id: doc.id,
    name: data.name || '',
    code: data.code,
    order: data.order || 0,
    rankWeight: data.rankWeight || 0,
    requiredRecognizedYearsFromHire: data.requiredRecognizedYearsFromHire ?? null,
    requiredYearsForNextPosition: data.requiredYearsForNextPosition ?? null,
    // V50: promotionRule 매핑 추가
    promotionRule: data.promotionRule,
    status: data.status || 'active',
    description: data.description,
    createdAt: data.createdAt,
    updatedAt: data.updatedAt,
    updatedBy: data.updatedBy,
  };
}

/**
 * PositionLevel을 Firestore 문서 형식으로 변환
 *
 * PositionLevel 객체를 Firestore에 저장할 수 있는 형식으로 변환합니다.
 *
 * @param positionLevel PositionLevel 객체
 * @returns Firestore 문서 데이터
 *
 * @example
 * const docData = mapPositionLevelToFirestore(positionLevel);
 * await setDoc(doc(db, 'positionLevels', id), docData);
 */
export function mapPositionLevelToFirestore(
  positionLevel: Partial<PositionLevel>
): Record<string, any> {
  const data: Record<string, any> = {};

  if (positionLevel.name !== undefined) data.name = positionLevel.name;
  if (positionLevel.code !== undefined) data.code = positionLevel.code;
  if (positionLevel.order !== undefined) data.order = positionLevel.order;
  if (positionLevel.rankWeight !== undefined) data.rankWeight = positionLevel.rankWeight;
  if (positionLevel.requiredRecognizedYearsFromHire !== undefined) {
    data.requiredRecognizedYearsFromHire = positionLevel.requiredRecognizedYearsFromHire;
  }
  if (positionLevel.requiredYearsForNextPosition !== undefined) {
    data.requiredYearsForNextPosition = positionLevel.requiredYearsForNextPosition;
  }
  // V50: promotionRule 저장 추가
  if (positionLevel.promotionRule !== undefined) {
    data.promotionRule = positionLevel.promotionRule;
  }
  if (positionLevel.status !== undefined) data.status = positionLevel.status;
  if (positionLevel.description !== undefined) data.description = positionLevel.description;
  if (positionLevel.createdAt !== undefined) data.createdAt = positionLevel.createdAt;
  if (positionLevel.updatedAt !== undefined) data.updatedAt = positionLevel.updatedAt;
  if (positionLevel.updatedBy !== undefined) data.updatedBy = positionLevel.updatedBy;

  return data;
}

/**
 * ============================================
 * 인정 근무연한 계산 로직 (향후 구현)
 * ============================================
 *
 * **인정 근무연한이란?**
 *
 * 정규직 입사일부터 계산한 근무 기간에 학력에 따른 가산 연한을 더한 값입니다.
 *
 * **계산 공식:**
 * ```
 * 인정 근무연한 = 정규직 근무 기간 + 학력 가산 연한
 * ```
 *
 * **학력 가산 연한 예시:**
 * - 고졸: 0년
 * - 전문학사 (2년제): 2년
 * - 학사 (4년제): 4년
 * - 석사: 6년
 * - 박사: 9년
 *
 * **예시:**
 * - A직원: 정규직 입사일 2015-01-01, 학력 학사 (4년제)
 *   - 정규직 근무 기간: 2025 - 2015 = 10년
 *   - 학력 가산 연한: 4년
 *   - 인정 근무연한: 10 + 4 = 14년
 *   - → 차장 진급 대상 (requiredRecognizedYearsFromHire: 13)
 *
 * **향후 구현 계획 (Chapter 5-2):**
 *
 * 1. Employee 타입에 학력 정보 필드 추가:
 *    - educationLevel: 'high_school' | 'associate' | 'bachelor' | 'master' | 'doctorate'
 *    - educationYearsCredit: number (학력 가산 연한)
 *
 * 2. 인정 근무연한 계산 함수 구현:
 *    ```ts
 *    function calculateRecognizedYearsFromHire(employee: Employee): number {
 *      const workYears = getYearsSince(employee.ngleFirstHireDate); // V35: hireDate → ngleFirstHireDate
 *      const educationCredit = employee.educationYearsCredit || 0;
 *      return workYears + educationCredit;
 *    }
 *    ```
 *
 * 3. 진급 대상자 자동 산출 기능:
 *    ```ts
 *    function getPromotionCandidates(
 *      employees: Employee[],
 *      positionLevels: PositionLevel[]
 *    ): PromotionCandidate[] {
 *      // 각 직원의 인정 근무연한 계산
 *      // 현재 직위보다 높은 직위 중 requiredRecognizedYearsFromHire 기준 충족 여부 확인
 *      // 진급 대상자 목록 반환
 *    }
 *    ```
 *
 * 4. 진급 대상자 관리 페이지 구현:
 *    - 진급 대상자 목록 표시
 *    - 현재 직위 → 진급 가능 직위
 *    - 인정 근무연한 vs 필요 연한 비교
 *    - 진급 승인/반려 기능
 */

/**
 * ============================================
 * 직위 가중치(rankWeight) 활용 예시
 * ============================================
 *
 * **1. 조직도에서 직위별 정렬:**
 * ```ts
 * // 높은 직위 → 낮은 직위 순으로 정렬
 * members.sort((a, b) => {
 *   const aWeight = positionLevels.find(p => p.name === a.positionTitle)?.rankWeight || 0;
 *   const bWeight = positionLevels.find(p => p.name === b.positionTitle)?.rankWeight || 0;
 *   return bWeight - aWeight; // 내림차순
 * });
 * ```
 *
 * **2. 직원 목록에서 직위 필터링:**
 * ```ts
 * // 차장 이상만 필터링
 * const seniorEmployees = employees.filter(emp => {
 *   const posLevel = positionLevels.find(p => p.name === emp.positionTitle);
 *   return posLevel && posLevel.rankWeight >= 4; // 차장 이상
 * });
 * ```
 *
 * **3. 승인 권한 체크:**
 * ```ts
 * // 본인보다 낮은 직위의 승인 요청만 처리 가능
 * function canApprove(approver: Employee, requester: Employee): boolean {
 *   const approverWeight = positionLevels.find(p => p.name === approver.positionTitle)?.rankWeight || 0;
 *   const requesterWeight = positionLevels.find(p => p.name === requester.positionTitle)?.rankWeight || 0;
 *   return approverWeight > requesterWeight;
 * }
 * ```
 */
